import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {
  BokkzSalarisClient,
  Dienstverband, LoonstaatEntity,
  PersoonGeslacht,
  PersoonLandcode,
  PersoonNationaliteit,
  ProfielDto, RequestDto,
  WerkgeverAangifteTijdvak,
  WerkgeverEntity,
  WerkgeverSettings,
  WerknemerEntity,
} from '../services/api.bokkz.salaris';
import {BokkzWageDataService} from '../services/bokkz-wage-data-service';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {ClientEntity} from '../services/api.bokkz.services';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {filter, finalize, Observable, shareReplay, tap} from 'rxjs';
import {dispatchSavedItemEvent} from '@snelstart/domain-logic';
import {right} from '@popperjs/core';
import {SharedUiTooltipTriggerModule} from '@snelstart/ui-tooltip-trigger';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BokkzAlgDataService} from "../services/bokkz-alg-data-service";

type PersoonFormGroup = FormGroup<{
  personeelsnummer: FormControl<string | null>;
  personeelsnummerVerloning: FormControl<string | null>;
  roepnaam: FormControl<string | null>;
  voorletters: FormControl<string | null>;
  tussenvoegsel: FormControl<string | null>;
  achternaam: FormControl<string | null>;
  naamVolledig: FormControl<string | null>;
  geslacht: FormControl<PersoonGeslacht | null>;
  geboortedatum: FormControl<Date | null>;
  leeftijd: FormControl<number | null>;
  datumIngangAOW: FormControl<Date | null>;
  burgerservicenummer: FormControl<string | null>;
  nationaliteit: FormControl<PersoonNationaliteit | null>;
  burgerlijkeStaat: FormControl<string | null>;
  tussenvoegselPartner: FormControl<string | null>;
  achternaamPartner: FormControl<string | null>;
  gebruikAchternaam: FormControl<string | null>;
}>;

type AdresFormGroup = FormGroup<{
  straatnaam: FormControl<string | null>;
  huisnummer: FormControl<number | null>;
  postcode: FormControl<string | null>;
  plaats: FormControl<string | null>;
  toevoeging: FormControl<string | null>;
  locatieomschrijving: FormControl<string | null>;
  landcode: FormControl<PersoonLandcode | null>;
  straatnaamBtl: FormControl<string | null>;
  huisnummerBtl: FormControl<string | null>;
  locatieomschrijvingBtl: FormControl<string | null>;
  postcodeBtl: FormControl<string | null>;
  woonplaatsBtl: FormControl<string | null>;
  regionaamBtl: FormControl<string | null>;
  telefoonnummer: FormControl<string | null>;
  emailadres: FormControl<string | null>;
}>;

type ExcassoFormGroup = FormGroup<{
  iban: FormControl<string | null>;
}>;

type ProfileFormGroup = FormGroup<{
  reset: FormControl<boolean>;
  profielId: FormControl<string | null>;
  verloningstijdvak: FormControl<WerkgeverAangifteTijdvak | null>;
  geboortedatum: FormControl<Date | null>;
  burgerservicenummer: FormControl<string | null>;
  nummerIkv: FormControl<number | null>;
  datumInDienst: FormControl<Date | null>;
  bepaaldeTijdContract: FormControl<boolean | null>;
  lhkToepassen: FormControl<boolean | null>;
  sectorCode: FormControl<string | null>;
  caoCode: FormControl<string | null>;
  typePensioenregeling: FormControl<string | null>;
  rechtOpVakantiegeld: FormControl<boolean | null>;
  vakantiegeldBijAanvang: FormControl<number | null>;
  urenPerWeek: FormControl<number | null>;
  normuren: FormControl<number | null>;
  periodeVrijval: FormControl<number | null>;
  periodeSalaris: FormControl<number | null>;
  grondslagComponenten: FormControl<number[]>;
  percentage: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-employee',
  templateUrl: './employee.component.html',
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiTabModule,
    SharedUiIconModule,
    SharedUiAlertModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiTooltipTriggerModule,
  ],
  styleUrl: './employee.component.css',
})
export class EmployeeComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public dialogVisible = false;
  @Input() public werknemerEntity?: WerknemerEntity;
  @Input() public wageId: any;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();
  protected readonly right = right;
  private sstRelatieId: any;
  public selectedTab = 0;
  public focusedElementId: string | null = null;
  public formStructure: any;
  public werkgeverEntity = {} as WerkgeverEntity;
  public dienstverband = {} as Dienstverband;
  public clientEntity = {} as ClientEntity;
  public defaultSectorCode: any;
  public defaultCaoCode: any;
  public defaultNormuren: any;
  private profilesLoaded = false;
  private profilesRequestInFlight = false;
  private lastWerkgeverFingerprint?: string;

  private readonly destroyRef = inject(DestroyRef);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzSalarisClient);
  private readonly dataService = inject(BokkzWageDataService);

  public persoonFg: PersoonFormGroup = new FormGroup({
    personeelsnummer: new FormControl<string | null>(null, {validators: [Validators.required]}),
    personeelsnummerVerloning: new FormControl<string | null>(null),
    roepnaam: new FormControl<string | null>(null),
    voorletters: new FormControl<string | null>(null, {validators: [Validators.required]}),
    tussenvoegsel: new FormControl<string | null>(null),
    achternaam: new FormControl<string | null>(null, {validators: [Validators.required]}),
    naamVolledig: new FormControl<string | null>(null),
    geslacht: new FormControl<PersoonGeslacht | null>(null, {validators: [Validators.required]}),
    geboortedatum: new FormControl<Date | null>(null, {validators: [Validators.required]}),
    leeftijd: new FormControl<number | null>(null),
    datumIngangAOW: new FormControl<Date | null>(null),
    burgerservicenummer: new FormControl<string | null>(null, {validators: [Validators.required]}),
    nationaliteit: new FormControl<PersoonNationaliteit | null>(null, {validators: [Validators.required]}),
    burgerlijkeStaat: new FormControl<string | null>(null),
    tussenvoegselPartner: new FormControl<string | null>(null),
    achternaamPartner: new FormControl<string | null>(null),
    gebruikAchternaam: new FormControl<string | null>(null),
  });

  public adresFg: AdresFormGroup = new FormGroup({
    straatnaam: new FormControl<string | null>(null, {validators: [Validators.required]}),
    huisnummer: new FormControl<number | null>(null, {validators: [Validators.required]}),
    postcode: new FormControl<string | null>(null, {validators: [Validators.required]}),
    plaats: new FormControl<string | null>(null, {validators: [Validators.required]}),
    toevoeging: new FormControl<string | null>(null),
    locatieomschrijving: new FormControl<string | null>(null),
    landcode: new FormControl<PersoonLandcode | null>(null),
    straatnaamBtl: new FormControl<string | null>(null),
    huisnummerBtl: new FormControl<string | null>(null),
    locatieomschrijvingBtl: new FormControl<string | null>(null),
    postcodeBtl: new FormControl<string | null>(null),
    woonplaatsBtl: new FormControl<string | null>(null),
    regionaamBtl: new FormControl<string | null>(null),
    telefoonnummer: new FormControl<string | null>(null),
    emailadres: new FormControl<string | null>(null),
  });

  public excassoFg: ExcassoFormGroup = new FormGroup({
    iban: new FormControl<string | null>(null),
  });

  public profileFg: ProfileFormGroup = new FormGroup({
    reset: new FormControl<boolean>(false, { nonNullable: true }),
    profielId: new FormControl<string | null>(null, {validators: [Validators.required]}),
    verloningstijdvak: new FormControl<WerkgeverAangifteTijdvak | null>(null),
    geboortedatum: new FormControl<Date | null>(null),
    burgerservicenummer: new FormControl<string | null>(null),
    nummerIkv: new FormControl<number | null>(null, {validators: [Validators.required]}),
    datumInDienst: new FormControl<Date | null>(null, {validators: [Validators.required]}),
    bepaaldeTijdContract: new FormControl<boolean | null>(null, {validators: [Validators.required]}),
    lhkToepassen: new FormControl<boolean | null>(null, {validators: [Validators.required]}),
    sectorCode: new FormControl<string | null>(null),
    caoCode: new FormControl<string | null>(null),
    typePensioenregeling: new FormControl<string | null>(null),
    rechtOpVakantiegeld: new FormControl<boolean | null>(null, {validators: [Validators.required]}),
    vakantiegeldBijAanvang: new FormControl<number | null>(null),
    urenPerWeek: new FormControl<number | null>(null, {validators: [Validators.required]}),
    normuren: new FormControl<number | null>(null),
    periodeVrijval: new FormControl<number | null>(null),
    periodeSalaris: new FormControl<number | null>(null, {validators: [Validators.required]}),
    grondslagComponenten: new FormControl<number[]>([], { nonNullable: true }),
    percentage: new FormControl<number | null>(null),
  }, {validators: [this.profileVakantiegeldValidator()]});

  readonly clientEntity$: Observable<ClientEntity> =
    this.algDataService.clientEntity$.pipe(
      filter((data): data is ClientEntity => !!data?.sstRelationId),
      tap(data => {
        if (this.sstRelatieId !== data.sstRelationId) {
          this.profilesLoaded = false;
          this.optionsProfielen = [];
        }
        this.sstRelatieId = data.sstRelationId;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werkgeverEntity$: Observable<WerkgeverEntity> =
    this.dataService.werkgeverEntity$.pipe(
      filter((data): data is WerkgeverEntity => !!data?.id),
      tap(data => {
        const werkgeverFingerprint = this.getWerkgeverFingerprint(data);
        if (this.lastWerkgeverFingerprint === werkgeverFingerprint) {
          return;
        }
        this.lastWerkgeverFingerprint = werkgeverFingerprint;

        this.werkgeverEntity = data;
        this.defaultNormuren = data.werkgeverGegevens?.normuren;
        const settingsList = data.werkgeverGegevens?.settings;
        const settings = settingsList && settingsList.length > 0
          ? settingsList[settingsList.length - 1]
          : undefined;
        this.defaultCaoCode = settings?.caoCode ?? undefined;
        this.defaultSectorCode = settings?.sectorCode ?? undefined;
        this.profileFg.patchValue(
          {
            verloningstijdvak: data.werkgeverGegevens?.aangifteTijdvak,
            normuren: data.werkgeverGegevens?.normuren ?? null,
            caoCode: settings?.caoCode ?? null,
            sectorCode: settings?.sectorCode ?? null,
          },
          {emitEvent: false}
        );
        if (settings) {
          this.getProfiles(settings);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  private getWerkgeverFingerprint(werkgever: WerkgeverEntity): string {
    const settingsList = werkgever.werkgeverGegevens?.settings;
    const settings = settingsList && settingsList.length > 0
      ? settingsList[settingsList.length - 1]
      : undefined;

    return JSON.stringify({
      id: werkgever.id,
      aangifteTijdvak: werkgever.werkgeverGegevens?.aangifteTijdvak,
      normuren: werkgever.werkgeverGegevens?.normuren,
      settings,
    });
  }

  public tooltips = {
    personeelsnummer: {
      icon: 'circle-info',
      tooltip: 'Hier geef je het unieke personeelsnummer op dat je organisatie gebruikt',
      placement: 'right',
    },
    voorletters: {
      icon: 'circle-info',
      tooltip: 'Bijvoorbeeld: J.A.',
      placement: 'right',
    },
    achternaam: {
      icon: 'circle-info',
      tooltip: 'Geboortenaam zoals deze op het identiteitsbewijs staat',
      placement: 'right',
    },
    geslacht: {
      icon: 'circle-info',
      tooltip: 'Selecteer uit de keuzelijst',
      placement: 'right',
    },
    geboortedatum: {
      icon: 'circle-info',
      tooltip: 'Geboortedatum zoals deze op het identiteitsbewijs staat vermeld',
      placement: 'right',
    },
    nationaliteit: {
      icon: 'circle-info',
      tooltip: 'Selecteer de nationaliteit zoals op het identiteitsbewijs staat vermeld',
      placement: 'right',
    },
    burgerservicenummer: {
      icon: 'circle-info',
      tooltip: 'BSN zoals deze op het identiteitsbewijs staat vermeld',
      placement: 'right',
    },

    // PROFIEL TAB
    profiel: {
      icon: 'circle-info',
      tooltip: 'Selecteer een profiel uit de keuzelijst of raadpleeg Kennisplein voor meer informatie',
      placement: 'right',
    },
    nummerIkv: {
      icon: 'circle-info',
      tooltip: 'Betreft het een actief dienstverband uit een andere salarisapplicatie, voer dan hier hetzelfde nummer IKV in. Raadpleeg Kennisplein voor meer informatie.',
      placement: 'right',
    },
    datumInDienst: {
      icon: 'circle-info',
      tooltip: 'De datum waarop de arbeidsovereenkomst is gestart.',
      placement: 'right',
    },
    contractduur: {
      icon: 'circle-info',
      tooltip: 'Selecteer uit de keuzelijst',
      placement: 'right',
    },
    urenPerWeek: {
      icon: 'circle-info',
      tooltip: 'Geef het aantal contracturen op conform arbeidsovereenkomst',
      placement: 'right',
    },
    periodeSalaris: {
      icon: 'circle-info',
      tooltip: 'Geef het vaste periodesalaris in wat behoort bij het aantal contracturen',
      placement: 'right',
    },
    lhk: {
      icon: 'circle-info',
      tooltip: 'Selecteer uit de keuzelijst',
      placement: 'right',
    },
    vakantiegeld: {
      icon: 'circle-info',
      tooltip: 'Selecteer uit de keuzelijst',
      placement: 'right',
    },
    vakantiegeldBijAanvang: {
      icon: 'circle-info',
      tooltip: 'Alleen van toepassing indien er in een andere salarisapplicatie vakantiegeld is gereserveerd',
      placement: 'right',
    },
    periodeVrijval: {
      icon: 'circle-info',
      tooltip: 'Selecteer uit de keuzelijst de periode waarin het vakantiegeld betaald moet worden. Opbouw van de reservering loopt automatisch door tot en met de vrijvalperiode',
      placement: 'right',
    },
  } as const;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public optionsJaNeeBepaaldeTijd: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Bepaalde tijd'},
    {value: false, label: 'Onbepaalde tijd'},
  ];

  public optionsGeslacht: SelectFormControlItem<string>[] = [
    {value: PersoonGeslacht.ONBEKEND, label: 'Onbekend'},
    {value: PersoonGeslacht.MAN, label: 'Man'},
    {value: PersoonGeslacht.VROUW, label: 'Vrouw'},
    {value: PersoonGeslacht.NIET_GESPECIFICEERD, label: 'Niet gespecificeerd'},
  ];

  public optionsProfielen: SelectFormControlItem<string>[] = [];

  public optionsNationaliteitenCode: SelectFormControlItem<string>[] = [
    {value: PersoonNationaliteit.NEDERLANDSE, label: 'Nederlandse'},
    {value: PersoonNationaliteit.AFGHAANSE, label: 'Afghaanse'},
    {value: PersoonNationaliteit.ALBANESE, label: 'Albanese'},
    {value: PersoonNationaliteit.ALGERIJNSE, label: 'Algerijnse'},
    {value: PersoonNationaliteit.AMERIKAANS_BURGER, label: 'Amerikaans burger'},
    {value: PersoonNationaliteit.AMERIKAANS_ONDERDAAN, label: 'Amerikaans onderdaan'},
    {value: PersoonNationaliteit.ANDORRESE, label: 'Andorrese'},
    {value: PersoonNationaliteit.ANGOLESE, label: 'Angolese'},
    {value: PersoonNationaliteit.ARGENTIJNSE, label: 'Argentijnse'},
    {value: PersoonNationaliteit.ARMEENSE, label: 'Armeense'},
    {value: PersoonNationaliteit.AUSTRALISCHE, label: 'Australische'},
    {value: PersoonNationaliteit.AZERBEIDZJAANSE, label: 'Azerbeidzjaanse'},
    {value: PersoonNationaliteit.BAHAMAANSE, label: 'Bahamaanse'},
    {value: PersoonNationaliteit.BAHREINSE, label: 'Bahreinse'},
    {value: PersoonNationaliteit.BARBADAANSE, label: 'Barbadaanse'},
    {value: PersoonNationaliteit.BELARUSSISCHE, label: 'Belarussische'},
    {value: PersoonNationaliteit.BELGISCHE, label: 'Belgische'},
    {value: PersoonNationaliteit.BELIZAANSE, label: 'Belizaanse'},
    {value: PersoonNationaliteit.BENGALESE, label: 'Bengalese'},
    {value: PersoonNationaliteit.BENINSE, label: 'Beninse'},
    {value: PersoonNationaliteit.BHUTAANSE, label: 'Bhutaanse'},
    {value: PersoonNationaliteit.BOLIVIAANSE, label: 'Boliviaanse'},
    {value: PersoonNationaliteit.BOTSWAANSE, label: 'Botswaanse'},
    {value: PersoonNationaliteit.BRAZILIAANSE, label: 'Braziliaanse'},
    {value: PersoonNationaliteit.BRITISH_NATIONAL_OVERSEAS, label: 'British National (overseas)'},
    {value: PersoonNationaliteit.BRITS_BURGER, label: 'Brits burger'},
    {value: PersoonNationaliteit.BRITS_ONDERDAAN, label: 'Brits onderdaan'},
    {value: PersoonNationaliteit.BRITS_OVERZEES_BURGER, label: 'Brits overzees burger'},
    {value: PersoonNationaliteit.BRUNEISE, label: 'Bruneise'},
    {value: PersoonNationaliteit.BULGAARSE, label: 'Bulgaarse'},
    {value: PersoonNationaliteit.BURGER_VAN_ANTIGUA_EN_BARBUDA, label: 'Burger van Antigua en Barbuda'},
    {value: PersoonNationaliteit.BURGER_VAN_BOSNIË_HERZEGOVINA, label: 'Burger van Bosnië-Herzegovina'},
    {
      value: PersoonNationaliteit.BURGER_VAN_BRITSE_AFHANKELIJKE_GEBIEDEN,
      label: 'Burger van Britse afhankelijke gebieden'
    },
    {value: PersoonNationaliteit.BURGER_VAN_CONGO, label: 'Burger van Congo'},
    {
      value: PersoonNationaliteit.BURGER_VAN_DE_BONDSREPUBLIEK_DUITSLAND,
      label: 'Burger van de Bondsrepubliek Duitsland'
    },
    {
      value: PersoonNationaliteit.BURGER_VAN_DE_VERENIGDE_ARABISCHE_EMIRATEN,
      label: 'Burger van de Verenigde Arabische Emiraten'
    },
    {
      value: PersoonNationaliteit.BURGER_VAN_DEMOCRATISCHE_REPUBLIEK_CONGO,
      label: 'Burger van Democratische Republiek Congo'
    },
    {value: PersoonNationaliteit.BURGER_VAN_DOMINICA, label: 'Burger van Dominica'},
    {value: PersoonNationaliteit.BURGER_VAN_SAINT_KITTS_EN_NEVIS, label: 'Burger van Saint Kitts en Nevis'},
    {
      value: PersoonNationaliteit.BURGER_VAN_SAINT_VINCENT_EN_DE_GRENADINES,
      label: 'Burger van Saint Vincent en de Grenadines'
    },
    {value: PersoonNationaliteit.BURGER_VAN_SÃO_TOMÉ_EN_PRINCIPE, label: 'Burger van São Tomé en Principe'},
    {value: PersoonNationaliteit.BURGER_VAN_TIMOR_LESTE, label: 'Burger van Timor Leste'},
    {value: PersoonNationaliteit.BURGER_VAN_TRINIDAD_EN_TOBAGO, label: 'Burger van Trinidad en Tobago'},
    {value: PersoonNationaliteit.BURKINESE, label: 'Burkinese'},
    {value: PersoonNationaliteit.BURUNDESE, label: 'Burundese'},
    {value: PersoonNationaliteit.CAMBODJAANSE, label: 'Cambodjaanse'},
    {value: PersoonNationaliteit.CANADESE, label: 'Canadese'},
    {value: PersoonNationaliteit.CENTRAAL_AFRIKAANSE, label: 'Centraal-Afrikaanse'},
    {value: PersoonNationaliteit.CHILEENSE, label: 'Chileense'},
    {value: PersoonNationaliteit.CHINESE, label: 'Chinese'},
    {value: PersoonNationaliteit.COLOMBIAANSE, label: 'Colombiaanse'},
    {value: PersoonNationaliteit.COMORESE, label: 'Comorese'},
    {value: PersoonNationaliteit.COSTA_RICAANSE, label: 'Costa Ricaanse'},
    {value: PersoonNationaliteit.CUBAANSE, label: 'Cubaanse'},
    {value: PersoonNationaliteit.CYPRISCHE, label: 'Cyprische'},
    {value: PersoonNationaliteit.DEENSE, label: 'Deense'},
    {value: PersoonNationaliteit.DJIBOUTIAANSE, label: 'Djiboutiaanse'},
    {value: PersoonNationaliteit.DOMINICAANSE, label: 'Dominicaanse'},
    {value: PersoonNationaliteit.ECUADORAANSE, label: 'Ecuadoraanse'},
    {value: PersoonNationaliteit.EGYPTISCHE, label: 'Egyptische'},
    {value: PersoonNationaliteit.EQUATORIAAL_GUINESE, label: 'Equatoriaal-Guinese'},
    {value: PersoonNationaliteit.ERITRESE, label: 'Eritrese'},
    {value: PersoonNationaliteit.ESTISCHE, label: 'Estische'},
    {value: PersoonNationaliteit.ESWATINISCHE, label: 'Eswatinische'},
    {value: PersoonNationaliteit.ETHIOPISCHE, label: 'Ethiopische'},
    {value: PersoonNationaliteit.FIJISCHE, label: 'Fijische'},
    {value: PersoonNationaliteit.FILIPIJNSE, label: 'Filipijnse'},
    {value: PersoonNationaliteit.FINSE, label: 'Finse'},
    {value: PersoonNationaliteit.FRANSE, label: 'Franse'},
    {value: PersoonNationaliteit.GABONESE, label: 'Gabonese'},
    {value: PersoonNationaliteit.GAMBIAANSE, label: 'Gambiaanse'},
    {value: PersoonNationaliteit.GEORGISCHE, label: 'Georgische'},
    {value: PersoonNationaliteit.GHANESE, label: 'Ghanese'},
    {value: PersoonNationaliteit.GRENADAANSE, label: 'Grenadaanse'},
    {value: PersoonNationaliteit.GRIEKSE, label: 'Griekse'},
    {value: PersoonNationaliteit.GUATEMALAANSE, label: 'Guatemalaanse'},
    {value: PersoonNationaliteit.GUINEE_BISSAUSE, label: 'Guinee-Bissause'},
    {value: PersoonNationaliteit.GUINESE, label: 'Guinese'},
    {value: PersoonNationaliteit.GUYAANSE, label: 'Guyaanse'},
    {value: PersoonNationaliteit.HAÏTIAANSE, label: 'Haïtiaanse'},
    {value: PersoonNationaliteit.HONDURESE, label: 'Hondurese'},
    {value: PersoonNationaliteit.HONGAARSE, label: 'Hongaarse'},
    {value: PersoonNationaliteit.IERSE, label: 'Ierse'},
    {value: PersoonNationaliteit.IJSLANDSE, label: 'IJslandse'},
    {value: PersoonNationaliteit.INDIASE, label: 'Indiase'},
    {value: PersoonNationaliteit.INDONESISCHE, label: 'Indonesische'},
    {value: PersoonNationaliteit.IRAAKSE, label: 'Iraakse'},
    {value: PersoonNationaliteit.IRAANSE, label: 'Iraanse'},
    {value: PersoonNationaliteit.ISRAËLISCHE, label: 'Israëlische'},
    {value: PersoonNationaliteit.ITALIAANSE, label: 'Italiaanse'},
    {value: PersoonNationaliteit.IVORIAANSE, label: 'Ivoriaanse'},
    {value: PersoonNationaliteit.JAMAICAANSE, label: 'Jamaicaanse'},
    {value: PersoonNationaliteit.JAPANSE, label: 'Japanse'},
    {value: PersoonNationaliteit.JEMENITISCHE, label: 'Jemenitische'},
    {value: PersoonNationaliteit.JORDAANSE, label: 'Jordaanse'},
    {value: PersoonNationaliteit.KAAPVERDISCHE, label: 'Kaapverdische'},
    {value: PersoonNationaliteit.KAMEROENSE, label: 'Kameroense'},
    {value: PersoonNationaliteit.KAZACHSE, label: 'Kazachse'},
    {value: PersoonNationaliteit.KENYAANSE, label: 'Kenyaanse'},
    {value: PersoonNationaliteit.KIRGIZISCHE, label: 'Kirgizische'},
    {value: PersoonNationaliteit.KIRIBATISCHE, label: 'Kiribatische'},
    {value: PersoonNationaliteit.KOEWEITSE, label: 'Koeweitse'},
    {value: PersoonNationaliteit.KOSOVAARSE, label: 'Kosovaarse'},
    {value: PersoonNationaliteit.KROATISCHE, label: 'Kroatische'},
    {value: PersoonNationaliteit.LAOTIAANSE, label: 'Laotiaanse'},
    {value: PersoonNationaliteit.LESOTHAANSE, label: 'Lesothaanse'},
    {value: PersoonNationaliteit.LETSE, label: 'Letse'},
    {value: PersoonNationaliteit.LIBANESE, label: 'Libanese'},
    {value: PersoonNationaliteit.LIBERIAANSE, label: 'Liberiaanse'},
    {value: PersoonNationaliteit.LIBISCHE, label: 'Libische'},
    {value: PersoonNationaliteit.LIECHTENSTEINSE, label: 'Liechtensteinse'},
    {value: PersoonNationaliteit.LITOUWSE, label: 'Litouwse'},
    {value: PersoonNationaliteit.LUXEMBURGSE, label: 'Luxemburgse'},
    {
      value: PersoonNationaliteit.MACEDONISCHE_BURGER_VAN_NOORD_MACEDONIË,
      label: 'Macedonische/Burger van Noord-Macedonië'
    },
    {value: PersoonNationaliteit.MALAGASSISCHE, label: 'Malagassische'},
    {value: PersoonNationaliteit.MALAWISCHE, label: 'Malawische'},
    {value: PersoonNationaliteit.MALDIVISCHE, label: 'Maldivische'},
    {value: PersoonNationaliteit.MALEISISCHE, label: 'Maleisische'},
    {value: PersoonNationaliteit.MALINESE, label: 'Malinese'},
    {value: PersoonNationaliteit.MALTESE, label: 'Maltese'},
    {value: PersoonNationaliteit.MAROKKAANSE, label: 'Marokkaanse'},
    {value: PersoonNationaliteit.MARSHALLEILANDSE, label: 'Marshalleilandse'},
    {value: PersoonNationaliteit.MAURITAANSE, label: 'Mauritaanse'},
    {value: PersoonNationaliteit.MAURITIAANSE, label: 'Mauritiaanse'},
    {value: PersoonNationaliteit.MEXICAANSE, label: 'Mexicaanse'},
    {value: PersoonNationaliteit.MICRONESISCHE, label: 'Micronesische'},
    {value: PersoonNationaliteit.MOLDAVISCHE, label: 'Moldavische'},
    {value: PersoonNationaliteit.MONEGASKISCHE, label: 'Monegaskische'},
    {value: PersoonNationaliteit.MONGOLISCHE, label: 'Mongolische'},
    {value: PersoonNationaliteit.MONTENEGRIJNSE, label: 'Montenegrijnse'},
    {value: PersoonNationaliteit.MOZAMBIKAANSE, label: 'Mozambikaanse'},
    {value: PersoonNationaliteit.MYANMARESE, label: 'Myanmarese'},
    {value: PersoonNationaliteit.NAMIBISCHE, label: 'Namibische'},
    {value: PersoonNationaliteit.NAURUAANSE, label: 'Nauruaanse'},
    {value: PersoonNationaliteit.NEPALESE, label: 'Nepalese'},
    {value: PersoonNationaliteit.NICARAGUAANSE, label: 'Nicaraguaanse'},
    {value: PersoonNationaliteit.NIEUW_ZEELANDSE, label: 'Nieuw-Zeelandse'},
    {value: PersoonNationaliteit.NIGERESE, label: 'Nigerese'},
    {value: PersoonNationaliteit.NIGERIAANSE, label: 'Nigeriaanse'},
    {value: PersoonNationaliteit.NOORD_KOREAANSE, label: 'Noord-Koreaanse'},
    {value: PersoonNationaliteit.NOORSE, label: 'Noorse'},
    {value: PersoonNationaliteit.OEKRAÏENSE, label: 'Oekraïense'},
    {value: PersoonNationaliteit.OEZBEEKSE, label: 'Oezbeekse'},
    {value: PersoonNationaliteit.OMAANSE, label: 'Omaanse'},
    {value: PersoonNationaliteit.OOSTENRIJKSE, label: 'Oostenrijkse'},
    {value: PersoonNationaliteit.PAKISTAANSE, label: 'Pakistaanse'},
    {value: PersoonNationaliteit.PALAUSE, label: 'Palause'},
    {value: PersoonNationaliteit.PANAMESE, label: 'Panamese'},
    {value: PersoonNationaliteit.PAPOEA_NIEUW_GUINESE, label: 'Papoea-Nieuw-Guinese'},
    {value: PersoonNationaliteit.PARAGUAYAANSE, label: 'Paraguayaanse'},
    {value: PersoonNationaliteit.PERUAANSE, label: 'Peruaanse'},
    {value: PersoonNationaliteit.POOLSE, label: 'Poolse'},
    {value: PersoonNationaliteit.PORTUGESE, label: 'Portugese'},
    {value: PersoonNationaliteit.QATARESE, label: 'Qatarese'},
    {value: PersoonNationaliteit.ROEMEENSE, label: 'Roemeense'},
    {value: PersoonNationaliteit.RUSSISCHE, label: 'Russische'},
    {value: PersoonNationaliteit.RWANDESE, label: 'Rwandese'},
    {value: PersoonNationaliteit.SAINT_LUCIAANSE, label: 'Saint Luciaanse'},
    {value: PersoonNationaliteit.SALOMONSEILANDSE, label: 'Salomonseilandse'},
    {value: PersoonNationaliteit.SALVADORAANSE, label: 'Salvadoraanse'},
    {value: PersoonNationaliteit.SAMOAANSE, label: 'Samoaanse'},
    {value: PersoonNationaliteit.SAN_MARINESE, label: 'San Marinese'},
    {value: PersoonNationaliteit.SAOEDI_ARABISCHE, label: 'Saoedi-Arabische'},
    {value: PersoonNationaliteit.SENEGALESE, label: 'Senegalese'},
    {value: PersoonNationaliteit.SERVISCHE, label: 'Servische'},
    {value: PersoonNationaliteit.SEYCHELSE, label: 'Seychelse'},
    {value: PersoonNationaliteit.SIERRA_LEOONSE, label: 'Sierra Leoonse'},
    {value: PersoonNationaliteit.SINGAPORESE, label: 'Singaporese'},
    {value: PersoonNationaliteit.SLOVEENSE, label: 'Sloveense'},
    {value: PersoonNationaliteit.SLOWAAKSE, label: 'Slowaakse'},
    {value: PersoonNationaliteit.SOEDANESE, label: 'Soedanese'},
    {value: PersoonNationaliteit.SOMALISCHE, label: 'Somalische'},
    {value: PersoonNationaliteit.SPAANSE, label: 'Spaanse'},
    {value: PersoonNationaliteit.SRI_LANKAANSE, label: 'Sri Lankaanse'},
    {value: PersoonNationaliteit.STAATLOOS, label: 'Staatloos'},
    {value: PersoonNationaliteit.SURINAAMSE, label: 'Surinaamse'},
    {value: PersoonNationaliteit.SYRISCHE, label: 'Syrische'},
    {value: PersoonNationaliteit.TADZJIEKSE, label: 'Tadzjiekse'},
    {value: PersoonNationaliteit.TAIWANESE, label: 'Taiwanese'},
    {value: PersoonNationaliteit.TANZANIAANSE, label: 'Tanzaniaanse'},
    {value: PersoonNationaliteit.THAISE, label: 'Thaise'},
    {value: PersoonNationaliteit.TOGOLESE, label: 'Togolese'},
    {value: PersoonNationaliteit.TONGAANSE, label: 'Tongaanse'},
    {value: PersoonNationaliteit.TSJADISCHE, label: 'Tsjadische'},
    {value: PersoonNationaliteit.TSJECHISCHE, label: 'Tsjechische'},
    {value: PersoonNationaliteit.TUNESISCHE, label: 'Tunesische'},
    {value: PersoonNationaliteit.TURKMEENSE, label: 'Turkmeense'},
    {value: PersoonNationaliteit.TURKSE, label: 'Turkse'},
    {value: PersoonNationaliteit.TUVALUAANSE, label: 'Tuvaluaanse'},
    {value: PersoonNationaliteit.UGANDESE, label: 'Ugandese'},
    {value: PersoonNationaliteit.URUGUAYAANSE, label: 'Uruguayaanse'},
    {value: PersoonNationaliteit.VANUATUAANSE, label: 'Vanuatuaanse'},
    {value: PersoonNationaliteit.VATICAANSE, label: 'Vaticaanse'},
    {value: PersoonNationaliteit.VENEZOLAANSE, label: 'Venezolaanse'},
    {value: PersoonNationaliteit.VIETNAMESE, label: 'Vietnamese'},
    {value: PersoonNationaliteit.ZAMBIAANSE, label: 'Zambiaanse'},
    {value: PersoonNationaliteit.ZIMBABWAANSE, label: 'Zimbabwaanse'},
    {value: PersoonNationaliteit.ZUID_AFRIKAANSE, label: 'Zuid-Afrikaanse'},
    {value: PersoonNationaliteit.ZUID_KOREAANSE, label: 'Zuid-Koreaanse'},
    {value: PersoonNationaliteit.ZUID_SOEDANESE, label: 'Zuid-Soedanese'},
    {value: PersoonNationaliteit.ZWEEDSE, label: 'Zweedse'},
    {value: PersoonNationaliteit.ZWITSERSE, label: 'Zwitserse'},
    {value: PersoonNationaliteit.ONBEKEND, label: 'Onbekend'},
  ];

  public optionsVrijval: SelectFormControlItem<number>[] = [
    {value: 1, label: 'Periode 1'},
    {value: 2, label: 'Periode 2'},
    {value: 3, label: 'Periode 3'},
    {value: 4, label: 'Periode 4'},
    {value: 5, label: 'Periode 5'},
    {value: 6, label: 'Periode 6'},
    {value: 7, label: 'Periode 7'},
    {value: 8, label: 'Periode 8'},
    {value: 9, label: 'Periode 9'},
    {value: 10, label: 'Periode 10'},
    {value: 11, label: 'Periode 11'},
    {value: 12, label: 'Periode 12'},
    {value: 99, label: 'Periodieke vrijval'},
  ];

  public optionsGrondslagComponenten = [
    {value: 1200, text: 'Salaris'},
    {value: 1201, text: 'Persoonlijke toeslag'},
    {value: 1400, text: 'Bonus'},
  ];

  ngOnInit(): void {
    this.clientEntity$.subscribe();
    this.werkgeverEntity$.subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const werknemerChange = changes['werknemerEntity'];
    if (!werknemerChange) {
      return;
    }

    const werknemer = werknemerChange.currentValue as WerknemerEntity | undefined;
    if (!werknemer) {
      return;
    }

    this.patchFromWerknemerInput(werknemer);
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.persoonFg.reset();
    this.adresFg.reset();
    this.excassoFg.reset();
    this.profileFg.reset();
    this.optionsProfielen = [];
  }

  public onClose() {
    this.persoonFg.reset();
    this.adresFg.reset();
    this.excassoFg.reset();
    this.profileFg.reset();
    this.dialogVisibleChange.emit(false);
  }

  public get isEditMode(): boolean {
    return !!this.werknemerEntity?.id;
  }

  public onSelectTab(number: number) {
    this.selectedTab = number;
  }

  private patchFromWerknemerInput(werknemer: WerknemerEntity): void {
    this.werknemerEntity = werknemer;
    const eersteDienstverband = werknemer.werknemerGegevens?.dienstverbanden?.at(0);
    const eersteFiscaal = eersteDienstverband?.fiscaleGegevens?.at(0);

    const persoon = werknemer.werknemerGegevens?.persoon;
    if (persoon) {
      this.persoonFg.patchValue(persoon, {emitEvent: false});
      this.adresFg.patchValue(persoon, {emitEvent: false});
      this.profileFg.patchValue(
        {
          geboortedatum: persoon.geboortedatum,
          burgerservicenummer: persoon.burgerservicenummer,
          lhkToepassen: eersteFiscaal?.loonheffingskorting ?? false,
        },
        {emitEvent: false}
      );
    }

    const bankrekening = werknemer.werknemerGegevens?.bankrekeningen;
    if (bankrekening) {
      this.excassoFg.patchValue({iban: bankrekening.iban ?? null}, {emitEvent: false});
    }

    if (!eersteDienstverband) {
      return;
    }

    this.dienstverband = eersteDienstverband;
    const ikv = eersteDienstverband.ikv;

    this.profileFg.patchValue(
      {
        reset: true,
        profielId: eersteDienstverband.profielId,
        nummerIkv: ikv != null ? Number(ikv) : null,
        datumInDienst: eersteDienstverband.datumInDienst,
        sectorCode: this.defaultSectorCode ?? null,
        caoCode: this.defaultCaoCode ?? null,
        bepaaldeTijdContract: eersteDienstverband.contractGegevensList?.at(0)?.bepaaldeTijd ?? null,
        urenPerWeek: eersteDienstverband.werkrooster?.at(0)?.aantalUrenPerWeek ?? null,
        normuren: this.defaultNormuren ?? null,
        periodeSalaris: eersteDienstverband.vasteVergoedingen?.at(0)?.bedrag ?? null,
        rechtOpVakantiegeld: eersteDienstverband.regelingen?.at(0) !== undefined,
      },
      {emitEvent: false}
    );
  }

  private profileVakantiegeldValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rechtOpVakantiegeld = control.get('rechtOpVakantiegeld')?.value;
      if (rechtOpVakantiegeld !== true) {
        return null;
      }

      const percentage = control.get('percentage')?.value;
      const periodeVrijval = control.get('periodeVrijval')?.value;
      const grondslagComponenten = control.get('grondslagComponenten')?.value;

      const hasGrondslag = Array.isArray(grondslagComponenten) && grondslagComponenten.length > 0;
      if (percentage == null || periodeVrijval == null || !hasGrondslag) {
        return {profielVakantiegeldOnvolledig: true};
      }

      return null;
    };
  }

  public getProfiles(settings: WerkgeverSettings) {
    if (this.profilesRequestInFlight) {
      return;
    }

    if (this.profilesLoaded) {
      return;
    }

    this.profilesRequestInFlight = true;
    this.bokkzClient
      .getProfielen(settings)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.profilesRequestInFlight = false;
        })
      )
      .subscribe({
        next: (result: string[]) => {
          this.optionsProfielen = result.map((value) => ({
            value: value,
            label: value,
          }));
          this.profilesLoaded = true;
        },
        error: (error) => {
          console.log(error);
        },
      });
  }

  private validateDatumInDienstNaGeboortedatum(): void {
    const geboortedatum = this.persoonFg.controls.geboortedatum.getRawValue();
    const datumInDienstControl = this.profileFg.controls.datumInDienst;

    const datumInDienst = datumInDienstControl.getRawValue();
    const currentErrors: ValidationErrors = {...(datumInDienstControl.errors || {})};

    delete currentErrors['geboortedatumNaDatumInDienst'];

    if (!geboortedatum || !datumInDienst) {
      datumInDienstControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
      return;
    }

    const geboorte = new Date(geboortedatum);
    const inDienst = new Date(datumInDienst);

    geboorte.setHours(0, 0, 0, 0);
    inDienst.setHours(0, 0, 0, 0);

    if (geboorte > inDienst) {
      currentErrors['geboortedatumNaDatumInDienst'] = 'Datum indiensttreding ligt voor geboortedatum, pas datum aan.';
    }

    datumInDienstControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }

  public handleSubmit(): void {
    this.validateDatumInDienstNaGeboortedatum();

    if (!this.persoonFg.valid || !this.adresFg.valid || !this.profileFg.valid) {
      this.persoonFg.markAllAsTouched();
      this.adresFg.markAllAsTouched();
      this.profileFg.markAllAsTouched();
      return;
    }

    const persoon = this.persoonFg.getRawValue();
    this.profileFg.patchValue({
      geboortedatum: persoon.geboortedatum,
      burgerservicenummer: persoon.burgerservicenummer
    })

    if (!this.profileFg.controls.reset.value) {
      this.getDienstverband().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (value) => {
          this.dienstverband = value;
          this.saveForm();
          this.persistWerknemer();
        },
        error: (error) => {
          console.log(error);
        },
      });
    } else {
      this.saveForm();
      this.persistWerknemer();
    }
  }

  public saveForm() {
    const persoon = this.persoonFg.getRawValue();
    const adres = this.adresFg.getRawValue();
    const excasso = this.excassoFg.getRawValue();

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current ?? this.werknemerEntity ?? ({} as WerknemerEntity);

      const nextWerknemer = {
        ...baseEntity,
        id: this.werknemerEntity?.id,
        taxId: persoon.burgerservicenummer,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          persoon: {
            ...baseEntity.werknemerGegevens?.persoon,
            personeelsnummer: persoon.personeelsnummer,
            personeelsnummerVerloning: persoon.personeelsnummerVerloning,
            roepnaam: persoon.roepnaam,
            voorletters: persoon.voorletters,
            tussenvoegsel: persoon.tussenvoegsel,
            achternaam: persoon.achternaam,
            naamVolledig: persoon.naamVolledig,
            geslacht: persoon.geslacht,
            geboortedatum: persoon.geboortedatum,
            leeftijd: persoon.leeftijd,
            datumIngangAOW: persoon.datumIngangAOW,
            burgerservicenummer: persoon.burgerservicenummer,
            nationaliteit: persoon.nationaliteit,

            straatnaam: adres.straatnaam,
            huisnummer: adres.huisnummer,
            postcode: adres.postcode,
            plaats: adres.plaats,

            toevoeging: adres.toevoeging,
            locatieomschrijving: adres.locatieomschrijving,
            landcode: adres.landcode,

            straatnaamBtl: adres.straatnaamBtl,
            huisnummerBtl: adres.huisnummerBtl,
            locatieomschrijvingBtl: adres.locatieomschrijvingBtl,
            postcodeBtl: adres.postcodeBtl,
            woonplaatsBtl: adres.woonplaatsBtl,
            regionaamBtl: adres.regionaamBtl,

            telefoonnummer: adres.telefoonnummer,
            emailadres: adres.emailadres,

            burgerlijkeStaat: persoon.burgerlijkeStaat,
            tussenvoegselPartner: persoon.tussenvoegselPartner,
            achternaamPartner: persoon.achternaamPartner,
            gebruikAchternaam: persoon.gebruikAchternaam,
          },
          bankrekeningen: {
            ...baseEntity.werknemerGegevens?.bankrekeningen,
            iban: excasso.iban,
          },
          dienstverbanden: [this.dienstverband],
        },
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }

  public persistWerknemer(): void {
    if (!this.werknemerEntity) {
      return;
    }
    const existingWerknemerId = this.werknemerEntity.id;

    const dto = {
      dienstverband: this.werknemerEntity.werknemerGegevens!.dienstverbanden![0]!,
      loonStaat: undefined,
      year: new Date().getFullYear(),
    } as RequestDto

    this.bokkzClient.getCalculatedLoonstaat(dto).subscribe({
      next: (value) => {
        const entity = {
          taxId: this.werknemerEntity!.taxId,
          year: dto.year,
          loonStaat: value
        } as LoonstaatEntity
        if (this.werknemerEntity!.werknemerGegevens!.dienstverbanden?.[0]) {
          this.werknemerEntity!.werknemerGegevens!.dienstverbanden[0].loonstaat = entity;
        }
      },
      complete: () => {
        this.bokkzClient.postOneEmployee(this.sstRelatieId, this.werknemerEntity).subscribe({
          next: (werknemer) => {
            if (!existingWerknemerId && werknemer.id) {
              dispatchSavedItemEvent({
                identifier: werknemer.id,
                result: werknemer,
              });
            }
            this.dataService.updateWerknemer(werknemer);
            this.upsertWerknemerListSorted(werknemer);
          },
          error: (error) => {
            console.log(error);
          },
        });
      }
    })
    this.onClose();
  }

  private upsertWerknemerListSorted(werknemer: WerknemerEntity): void {
    const currentList = [...(this.dataService.werknemerList.getValue() ?? [])];
    const existingIndex = currentList.findIndex((item) => item.id === werknemer.id);

    if (existingIndex >= 0) {
      currentList[existingIndex] = werknemer;
    } else {
      currentList.push(werknemer);
    }

    currentList.sort((a, b) => {
      const aNummer = a.werknemerGegevens?.persoon?.personeelsnummer ?? '';
      const bNummer = b.werknemerGegevens?.persoon?.personeelsnummer ?? '';

      if (!aNummer && !bNummer) return 0;
      if (!aNummer) return 1;
      if (!bNummer) return -1;

      return aNummer.localeCompare(bNummer, undefined, { numeric: true, sensitivity: 'base' });
    });

    this.dataService.updateWerknemerList(currentList);
  }

  public getDienstverband(): Observable<Dienstverband> {
    return this.bokkzClient
      .getDienstverband(this.profileFg.getRawValue() as ProfielDto)
      .pipe(takeUntilDestroyed(this.destroyRef));
  }
}
